import { Injectable, Injector, OnDestroy, runInInjectionContext } from "@angular/core";
import { Firestore, collection, CollectionReference, doc, getDoc, getDocs, orderBy, query, where } from "@angular/fire/firestore";
import { EMPTY, from, interval, merge, Observable, of, Subject, throwError, timer } from "rxjs";
import { catchError, defaultIfEmpty, distinctUntilChanged, filter, map, shareReplay, switchMap, take, takeUntil, tap, timeout } from "rxjs/operators";

import { Data } from "@angular/router";
import { CustomDatePipe } from "src/app/pipe/custom.datepipe";
import { LuckyNumberService } from "src/app/services/lucky-number.service";
import { PaymentService } from "src/app/services/payment.service";
import { CatchErrorService } from "@core/catch-error.service";
import { SyndicateRepositoryService } from "./syndicate-repository.service";
import { AuthService } from "@core/auth.service";

import { StoreRepositoryService } from "../store/store-repository.service";
import {
  excelLabelToIndex,
  isExcelLineKey,
} from "./utils/excel-line-keys.util";

type AddonCustomerPurchaseEntry =
  | string
  | number
  | {
      is_jackpot_trigger_purchase?: boolean;
      customer_purchase_id?: string | number;
      encrypted_customer_purchase_id?: string | number;
      encrypted_cp_id?: string | number;
      encrypt_id?: string | number;
      customerPurchaseId?: string | number;
      cp_id?: string | number;
      child_customer_purchase_id?: string | number;
      id?: string | number;
    };

@Injectable({ providedIn: null })
export class SyndicateTicketFetchService implements OnDestroy {
  private unsubscribe$ = new Subject<void>();

  private addonOfferCpIdsArray: string[] = [];
  private currentAddonOfferIndex = 0;
  private addonOfferIdLength = 0;

  private addOnOfferProducsts: Data[] = [];
  private baseOfferTicketGenerated = false;

  public selectedNumbers: Data = {};
  public selectedNumbersAlready: Data = {};
  public selectedGoldRingLines: Data = {};
  public alreadySelectedGRLines: Data = {};
  public realname: string;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private firestore: Firestore,
    private paymentService: PaymentService,
    private catchService: CatchErrorService,
    private luckyNumberService: LuckyNumberService,
    private syndicateService: SyndicateRepositoryService,
    private authService: AuthService,
    private datepipe: CustomDatePipe,
    private injector: Injector
  ) {}

  private getDocInContext(ref: ReturnType<typeof doc>) {
    return runInInjectionContext(this.injector, () => getDoc(ref));
  }

  private getDocsInContext(q: ReturnType<typeof query>) {
    return runInInjectionContext(this.injector, () => getDocs(q));
  }

  destroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  fetchTicketData(
    userId: string,
    syndicateObj: Data,
    syndicateDetails: Data,
    paymentService: PaymentService,
    storeService: StoreRepositoryService
  ): Observable<Data> {
  
    // CASE 1: No syndicateObj (Sales journey case)
    if (!syndicateObj) {
      const encryptedId = paymentService.salesJourneyCustomerPurchaseId$.getValue();
  
      return interval(5000).pipe(
        timeout({ each: 46000 }),
        switchMap(() => paymentService.getCustomerPurchaseDetailsFromEncryptedCpId(encryptedId)),
        filter((data: Data) =>
          data?.processed_status !== undefined &&
          data?.processed_status !== null &&
          (
            data?.api_response?.response?.encrypt_syndicate_id !== "0" ||
            data?.processed_status === -1
          )
        ),
        take(1),
        switchMap((data: Data) => {
  
          const encrypt_syndicate_id = data?.api_response?.response?.encrypt_syndicate_id;
          const processed_status = data?.processed_status;
  
          if (processed_status === -1) {
            return throwError(() => ({
              type: "PAYMENT_FAILED",
              message: data?.payment_response_description || "Payment failed"
            }));
          }
  
          if (processed_status === 2 && encrypt_syndicate_id) {
            return this.getTicketData(userId, encrypt_syndicate_id, syndicateDetails);
          }
  
          return EMPTY;
        }),
        catchError((err) => throwError(() => err))
      );
    }
  
    // CASE 2: syndicateObj has request id
    if (syndicateObj?.pwa_syndicate_request_id) {
      return storeService.getSyndicateRequestId(syndicateObj?.pwa_syndicate_request_id).pipe(
        switchMap((resData: Data) => {
          const encryptId = resData?.encrypt_syndicate_id;
          if (!encryptId) return throwError(() => "Missing encrypt_syndicate_id");
          return this.getTicketData(userId, encryptId, syndicateDetails);
        }),
        catchError((err) => throwError(() => err))
      );
    }
  
    return throwError(() => "Invalid fetchTicketData input");
  }

  // ---------------- FIRESTORE READ: customer_purchase ----------------
  private fetchCustomerPurchaseSnapshot(
    userId: string,
    customerPurchaseId: string
  ): Observable<Data | null> {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline || !userId || !customerPurchaseId) {
          return of(null);
        }

        const customerPurchaseDocRef = doc(
          this.firestore,
          `users/${userId}/customer_purchase/${customerPurchaseId}`
        );

        return from(this.getDocInContext(customerPurchaseDocRef)).pipe(
          switchMap((docSnap: Data) => {
            if (docSnap?.exists?.()) {
              return of(docSnap.data() as Data);
            }

            const customerPurchaseCollectionRef = collection(
              this.firestore,
              `users/${userId}/customer_purchase`
            ) as CollectionReference;

            const numericCpId = Number(customerPurchaseId);
            const lookupQueries = [
              query(customerPurchaseCollectionRef, where("encrypted_customer_purchase_id", "==", customerPurchaseId)),
              query(customerPurchaseCollectionRef, where("encrypt_id", "==", customerPurchaseId)),
              query(customerPurchaseCollectionRef, where("customer_purchase_id", "==", customerPurchaseId)),
            ];

            if (!Number.isNaN(numericCpId)) {
              lookupQueries.push(
                query(customerPurchaseCollectionRef, where("customer_purchase_id", "==", numericCpId))
              );
            }

            return from(Promise.all(lookupQueries.map((q) => this.getDocsInContext(q)))).pipe(
              map((querySnapshots: Data[]) => {
                const firstMatch = querySnapshots.find((snap) => snap?.docs?.length > 0)?.docs?.[0];
                return firstMatch ? (firstMatch.data() as Data) : null;
              })
            );
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      catchError(() => of(null))
    );
  }

  getSyndicateIdFromCustomerPurchase(
    userId: string,
    customerPurchaseId: string,
    captureAddons = false
  ): Observable<string> {
    return this.fetchCustomerPurchaseSnapshot(userId, customerPurchaseId).pipe(
      map((cp_response) => {
        if (!cp_response) {
          return null;
        }

        if (captureAddons && cp_response?.add_on?.length > 0) {
          const normalizedAddonCpIds = this.normalizeAddonCpIds(cp_response.add_on);
          this.paymentService.addOnOffersCustomerPurchaseIds$.next(
            normalizedAddonCpIds.length ? normalizedAddonCpIds : null
          );
        }

        const encryptSyndicateId = cp_response?.api_response?.response?.encrypt_syndicate_id;
        return this.isValidEncryptSyndicateId(encryptSyndicateId) ? encryptSyndicateId : null;
      })
    );
  }

  // ---------------- FIRESTORE READ: managed_tickets/{id}/products ----------------
  getSyndicateProduct(userId: string, id: string) {
    if (!id || !userId) return of(null);

    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return of(null);

        const productsRef = collection(
          this.firestore,
          `users/${userId}/managed_tickets/${id}/products`
        ) as CollectionReference;

        const q = query(productsRef, orderBy("product_name", "desc"));

        return from(this.getDocsInContext(q)).pipe(
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1),
      map((snapshot: Data) => {
        if (!snapshot) return [];
        const docs = "docs" in snapshot ? snapshot.docs : [];
        return docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      })
    );
  }

  // ---------------- WRAPPER ----------------
  getTicketData(userId: string, syndicateEncryptedId: string, syndicateDetails: Data) {
    return this.getSyndicateProduct(userId, syndicateEncryptedId).pipe(
      map((productDetails) => {
        const syndicateTicketData = { product: productDetails } as Data;
        const prepared = this.prepareTicketsData(syndicateTicketData, syndicateDetails);
        prepared?.product?.forEach((product: Data) => {
          product.encrypt_syndicate_id = syndicateEncryptedId;
        });
        return prepared;
      }),
      switchMap((prepared) =>
        this.fetchSyndicateNameById(syndicateEncryptedId).pipe(
          map((syndicateName) => {
            if (syndicateName) {
              prepared?.product?.forEach((product: Data) => {
                product.syndicate_name = syndicateName;
              });
            }
            return prepared;
          }),
          defaultIfEmpty(prepared),
          catchError(() => of(prepared))
        )
      )
    );
  }

  private fetchSyndicateNameById(encryptSyndicateId: string): Observable<string | null> {
    if (!this.isValidEncryptSyndicateId(encryptSyndicateId)) {
      return of(null);
    }

    return this.paymentService.getSyndicateInfoWithProducts(encryptSyndicateId).pipe(
      take(1),
      timeout(2000),
      map((detail: Data) => {
        const info = detail?.[0]?.data?.();
        return info?.syndicate_name ?? info?.ps_name ?? null;
      }),
      defaultIfEmpty(null),
      catchError(() => of(null))
    );
  }

  resetAddonState() {
    this.addOnOfferProducsts = [];
    this.baseOfferTicketGenerated = false;
    this.addonOfferCpIdsArray = [];
    this.currentAddonOfferIndex = 0;
    this.addonOfferIdLength = 0;
  }

  getAddonCpIdsFromSnapshot(cpData: Data | null): string[] {
    if (!cpData || !Object.prototype.hasOwnProperty.call(cpData, "add_on")) {
      return [];
    }
    const addonEntries = Array.isArray(cpData.add_on) ? cpData.add_on : [];
    return this.normalizeAddonCpIds(addonEntries);
  }

  /**
   * Listener first, getDocs poll as fallback. Both stop when `stop$` emits (shared 15s deadline).
   */
  waitForTicketProducts(
    userId: string,
    syndicateId: string,
    stop$: Observable<unknown>
  ): Observable<Data[]> {
    if (!userId || !this.isValidEncryptSyndicateId(syndicateId)) {
      return EMPTY;
    }

    const hasProducts = (products: Data[] | null | undefined): products is Data[] =>
      Array.isArray(products) && products.length > 0;

    return merge(
      this.paymentService.watchSyndicateProducts(syndicateId).pipe(
        filter(hasProducts),
        catchError(() => EMPTY)
      ),
      timer(0, 1000).pipe(
        switchMap(() =>
          this.getSyndicateProduct(userId, syndicateId).pipe(catchError(() => of([])))
        ),
        filter(hasProducts),
        catchError(() => EMPTY)
      )
    ).pipe(take(1), takeUntil(stop$));
  }

  watchAddonTicketProducts(
    userId: string,
    customerPurchaseId: string,
    syndicateDetails: Data,
    stop$: Observable<unknown>
  ): Observable<Data[]> {
    if (!userId || !customerPurchaseId) {
      return of([]);
    }

    // Prefer exact-doc listener; fall back to snapshot lookup + poll (addon CP ids are often not the doc path).
    const fromWatch$ = this.paymentService.watchCustomerPurchaseDoc(customerPurchaseId).pipe(
      filter((syndicateId): syndicateId is string => this.isValidEncryptSyndicateId(syndicateId)),
      take(1),
      switchMap((addonSyndicateId) =>
        this.waitForTicketProducts(userId, addonSyndicateId, stop$).pipe(
          map((products) => {
            const prepared = this.prepareTicketsData({ product: products }, syndicateDetails);
            prepared?.product?.forEach((product: Data) => {
              product.encrypt_syndicate_id = addonSyndicateId;
            });
            return prepared?.product ?? [];
          })
        )
      ),
      catchError(() => EMPTY)
    );

    const fromLookup$ = this.loadAddonTicketProducts(userId, customerPurchaseId, syndicateDetails).pipe(
      map((products) => (Array.isArray(products) ? products : [])),
      catchError(() => EMPTY)
    );

    return merge(fromWatch$, fromLookup$).pipe(
      filter((products) => Array.isArray(products) && products.length > 0),
      take(1),
      takeUntil(stop$),
      catchError(() => of([]))
    );
  }

  /**
   * Resolves add-on CP ids from the base purchase: listener + immediate poll fallback.
   * Stops when `stop$` emits.
   */
  watchAddonCpIds(
    userId: string,
    baseCustomerPurchaseId: string,
    stop$: Observable<unknown>
  ): Observable<string[]> {
    if (!userId || !baseCustomerPurchaseId) {
      return of([]);
    }

    const fromWatch$ = this.paymentService.watchCustomerPurchaseSnapshot(baseCustomerPurchaseId).pipe(
      map((data) => this.getAddonCpIdsFromSnapshot(data)),
      filter((cpIds) => cpIds.length > 0),
      catchError(() => EMPTY)
    );

    const fromPoll$ = timer(0, 1000).pipe(
      switchMap(() =>
        this.readAddonCpIdsFromCustomerPurchase(userId, baseCustomerPurchaseId).pipe(
          catchError(() => EMPTY)
        )
      ),
      map(({ cpIds, addOnResolved, expectedCount }) => ({ cpIds, addOnResolved, expectedCount })),
      filter(
        (result) =>
          result.addOnResolved &&
          (result.expectedCount === 0 || result.cpIds.length >= result.expectedCount)
      ),
      map(({ cpIds }) => cpIds),
      filter((cpIds) => cpIds.length > 0),
      catchError(() => EMPTY)
    );

    return merge(fromWatch$, fromPoll$).pipe(takeUntil(stop$), catchError(() => of([] as string[])));
  }

  // ---------------- TRANSFORM PRODUCT DATA ----------------
  prepareTicketsData(syndicateTicketData: Data, syndicateDetails: Data) {
    try {
      if (syndicateTicketData?.product) {
        syndicateTicketData?.product?.map((ticketData) => {
          const tickets = [];
          if (!ticketData) return;

          let tktSelCount = 0;
          let goldRingTktSelCount = 0;

          const nextDrawDate: Date = ticketData?.next_draw_date ? ticketData?.next_draw_date?.toDate() : new Date();

          if (ticketData?.selected_numbers) {
            const productId = ticketData?.id;
            const drawSelected = this.datepipe.transform(nextDrawDate, "yyyy-MM-dd");

            const selectedNumbersDrawBased = ticketData?.selected_numbers[drawSelected]
              ? ticketData?.selected_numbers[drawSelected]
              : {};

            this.selectedNumbers[productId] = JSON.parse(JSON.stringify(selectedNumbersDrawBased));
            this.selectedNumbersAlready = JSON.parse(JSON.stringify(this.selectedNumbers));

            for (const selKey in this.selectedNumbers[productId]) {
              tktSelCount += this.selectedNumbers[productId][selKey].length;
            }

            ticketData.alreadySelectedCount = tktSelCount;
          }

          if (ticketData?.gold_ring) {
            const productId = ticketData?.id;

            this.selectedGoldRingLines.gold_ring = {};
            this.selectedGoldRingLines.gold_ring.lines = ticketData.gold_ring.lines;

            this.alreadySelectedGRLines[productId] = JSON.parse(
              JSON.stringify(this.selectedGoldRingLines.gold_ring.lines)
            );

            goldRingTktSelCount += ticketData?.gold_ring?.lines?.length;
            ticketData.alreadyGoldRingSelectedCount = goldRingTktSelCount;
          }

          let currentDate: Date = new Date();

          this.luckyNumberService
            .getLotteryData(ticketData?.product_name)
            ?.pipe(takeUntil(this.unsubscribe$))
            .subscribe((ref: Data) => {
              this.realname = ref?.real_name;
              let drawCloseTime = ref?.draw_close_time ? ref?.draw_close_time : "11:59:00";

              const isDrawDateExpired = this.luckyNumberService.isDrawDateExpired(
                nextDrawDate,
                currentDate,
                drawCloseTime
              );

              ticketData.isExpired = isDrawDateExpired;
            });

          let raffleCount = 0;

          Object.keys(ticketData)
            .filter((x) => isExcelLineKey(x))
            .sort((a, b) => excelLabelToIndex(a) - excelLabelToIndex(b))
            .map((ticketRow) => {
              if (ticketData[ticketRow]) {
                const getRaffleNum = this.getRaffleNumbers(ticketData, ticketRow);

                if (getRaffleNum && Array.isArray(getRaffleNum) && getRaffleNum?.length > 0) {
                  raffleCount++;
                }

                tickets.push({
                  regularNumbers: this.getRegularNumbers(ticketData, ticketRow),
                  bonusNumbers: this.getBonusNumbers(ticketData, ticketRow),
                  raffle: getRaffleNum,
                  eye: this.getEyeLink(ticketData, ticketRow),
                });

                ticketData.ticketLines = tickets;
              }
            });

          const raffleExist = ticketData.total_lines === raffleCount ? true : false;

          if (!raffleExist && syndicateDetails?.id) {
            this.syndicateService
              ?.getRaffleNum(syndicateDetails.id)
              ?.pipe(
                takeUntil(this.unsubscribe$),
                catchError((err) => {
                  this.authService.errorHandlerLogs("Error getting raffle numbers:", err);
                  return of(null);
                })
              )
              .subscribe();
          }
        });
      }

      return syndicateTicketData;
    } catch (e) {
      return e;
    }
  }

  private getBonusNumbers(ticket: Data, key: string) {
    return ticket[key]
      .filter((x) => typeof x === "string" && x?.length < 6 && x.search("B") === 0)
      .map((filteredData) => filteredData?.split("_")[1]);
  }

  private getRegularNumbers(ticket: Data, key: string) {
    return ticket[key]
      .filter((x) => x?.search("RG") === 0 && x?.length < 7)
      .map((filteredData) => filteredData?.split("_")[1]);
  }

  private getRaffleNumbers(ticket: Data, key: string) {
    return ticket[key]
      .filter((x) => typeof x === "string" && x?.length > 6 && x?.search("R") === 0)
      .map((filteredData) => filteredData?.split("_")[1]);
  }

  private getEyeLink(ticket: Data, key: string) {
    if (!ticket || !key) return;
    return ticket[key]?.find((x) => typeof x === "string" && (x?.search("https") === 0 || x?.search("png") === 0));
  }

  trackCustomerPurchaseDocument(
    userId: string,
    isSalesJourney: boolean,
    isCashPurchase: boolean,
    addOnOfferCpId?
  ): Observable<string> {
  
    const purchaseID$ = addOnOfferCpId
      ? of(addOnOfferCpId)
      : isSalesJourney
        ? this.paymentService.salesJourneyCustomerPurchaseId$
        : isCashPurchase
          ? this.paymentService.cashPurchaseCustomerPurchaseId$
          : of(null);
  
    return purchaseID$.pipe(
      distinctUntilChanged(),
      filter((customerPurchaseId: string) => !!customerPurchaseId),
      take(1),
  
      switchMap((customerPurchaseId: string) => {
        const captureAddons = !addOnOfferCpId && (isSalesJourney || isCashPurchase);
        return interval(1000).pipe(
          takeUntil(this.destroy$),
          timeout({
            first: 45000
          }),
          switchMap(() =>
            this.getSyndicateIdFromCustomerPurchase(userId, customerPurchaseId, captureAddons)
          ),
          filter((syndicateId: string) => this.isValidEncryptSyndicateId(syndicateId)),
          take(1)
        );
      }),
  
      catchError((err) => throwError(() => err))
    );
  }

  trackCustomerPurchaseDocumentFromAddCard(): Observable<string> {
    return this.paymentService.cashPurchaseAddCardSyndicateId$.pipe(
      filter((syndicateId: string) => !!syndicateId),
      take(1),
      catchError((err) => throwError(() => err))
    );
  }

  updateSyndicateViewFromSyndicateId(
    userId: string,
    syndicateId: string,
    syndicateDetails: Data,
    winMoreStatus: boolean,
    includeAddons = false,
    baseCustomerPurchaseId?: string
  ): Observable<{
    syndicateId: string;
    ticketData: Data;
    isWinMore: boolean;
  }> {
    if (!this.isValidEncryptSyndicateId(syndicateId)) return EMPTY;

    const primeAddons$ =
      includeAddons && baseCustomerPurchaseId
        ? this.primeAddonIdsFromCustomerPurchase(userId, baseCustomerPurchaseId)
        : of(null);

    return primeAddons$.pipe(
      switchMap(() =>
        this.getTicketData(userId, syndicateId, syndicateDetails).pipe(
          switchMap((ticketRes: Data) => {
            ticketRes?.product?.forEach((res: Data) => {
              res.isWinMore = winMoreStatus === true ? true : false;
            });

            const isWinMore = ticketRes?.product?.[0]?.isWinMore;

            if (!includeAddons || !baseCustomerPurchaseId) {
              return of({
                syndicateId,
                ticketData: ticketRes,
                isWinMore
              });
            }

            return this.resolveAddonCpIdsForTicket(userId, baseCustomerPurchaseId).pipe(
              switchMap((addonCpIds) =>
                this.mergeBaseTicketWithAddons(
                  userId,
                  syndicateId,
                  syndicateDetails,
                  ticketRes,
                  isWinMore,
                  addonCpIds
                )
              )
            );
          })
        )
      ),
      catchError((err) => throwError(() => err))
    );
  }

  private primeAddonIdsFromCustomerPurchase(
    userId: string,
    baseCustomerPurchaseId: string
  ): Observable<null> {
    return this.getSyndicateIdFromCustomerPurchase(userId, baseCustomerPurchaseId, true).pipe(
      take(1),
      catchError((err) => {
        this.authService.errorHandlerLogs(
          "Failed to prime addon customer purchase IDs from customer purchase:",
          err
        );
        // Best-effort priming; resolveAddonCpIdsForTicket will poll for addon IDs independently.
        return of(null);
      }),
      map(() => null)
    );
  }

  private resolveAddonCpIdsForTicket(
    userId: string,
    baseCustomerPurchaseId?: string
  ): Observable<string[]> {
    if (!baseCustomerPurchaseId) {
      return of([]);
    }

    const pollIntervalMs = 2000;
    const maxPollAttempts = 20;

    const isAddonSetReady = (result: {
      cpIds: string[];
      expectedCount: number;
      addOnResolved: boolean;
    }): boolean =>
      result.addOnResolved &&
      (result.expectedCount === 0 || result.cpIds.length >= result.expectedCount);

    const publishAddonCpIds = (cpIds: string[]): void => {
      if (cpIds.length) {
        this.paymentService.addOnOffersCustomerPurchaseIds$.next(cpIds);
      }
    };

    const pollForAddonCpIds = (): Observable<string[]> =>
      interval(pollIntervalMs).pipe(
        take(maxPollAttempts),
        switchMap(() => this.readAddonCpIdsFromCustomerPurchase(userId, baseCustomerPurchaseId)),
        tap(({ cpIds }) => publishAddonCpIds(cpIds)),
        filter(isAddonSetReady),
        take(1),
        map(({ cpIds }) => cpIds),
        defaultIfEmpty([] as string[])
      );

    return this.readAddonCpIdsFromCustomerPurchase(userId, baseCustomerPurchaseId).pipe(
      switchMap((initial) => {
        if (isAddonSetReady(initial)) {
          publishAddonCpIds(initial.cpIds);
          return of(initial.cpIds);
        }
        return pollForAddonCpIds();
      }),
      catchError(() => of([] as string[]))
    );
  }

  private readAddonCpIdsFromCustomerPurchase(
    userId: string,
    baseCustomerPurchaseId: string
  ): Observable<{ cpIds: string[]; expectedCount: number; addOnResolved: boolean }> {
    return this.fetchCustomerPurchaseSnapshot(userId, baseCustomerPurchaseId).pipe(
      map((cpResponse) => {
        if (!cpResponse) {
          return { cpIds: [], expectedCount: 0, addOnResolved: false };
        }

        if (Object.prototype.hasOwnProperty.call(cpResponse, "add_on")) {
          const addonEntries = Array.isArray(cpResponse.add_on) ? cpResponse.add_on : [];
          const ticketAddons = addonEntries.filter((entry) => this.shouldIncludeAddonOnTicket(entry));
          const cpIds = this.normalizeAddonCpIds(ticketAddons);

          return {
            cpIds,
            expectedCount: ticketAddons.length,
            addOnResolved: true,
          };
        }

        // No add_on on a completed purchase means no addons were selected.
        if (this.isCustomerPurchaseReady(cpResponse)) {
          return { cpIds: [], expectedCount: 0, addOnResolved: true };
        }

        return { cpIds: [], expectedCount: 0, addOnResolved: false };
      }),
      catchError(() => of({ cpIds: [], expectedCount: 0, addOnResolved: false }))
    );
  }

  private isCustomerPurchaseReady(cpResponse: Data): boolean {
    return (
      cpResponse?.processed_status === 2 ||
      this.isValidEncryptSyndicateId(cpResponse?.api_response?.response?.encrypt_syndicate_id)
    );
  }

  private mergeBaseTicketWithAddons(
    userId: string,
    syndicateId: string,
    syndicateDetails: Data,
    ticketRes: Data,
    isWinMore: boolean,
    addonCpIds: string[]
  ): Observable<{ syndicateId: string; ticketData: Data; isWinMore: boolean }> {
    if (!this.baseOfferTicketGenerated) {
      this.baseOfferTicketGenerated = true;
      const baseTicketData = ticketRes;

      if (!addonCpIds?.length) {
        return of({
          syndicateId,
          ticketData: baseTicketData,
          isWinMore
        });
      }

      this.addonOfferCpIdsArray = addonCpIds;
      this.addonOfferIdLength = addonCpIds.length;
      this.currentAddonOfferIndex = 0;
      this.addOnOfferProducsts = [];

      return this.fetchAddonOffersSequentially(userId, syndicateDetails).pipe(
        map((addonProducts: Data[]) => {
          if (addonProducts?.length) {
            baseTicketData.product.push(...addonProducts);
          }

          return {
            syndicateId,
            ticketData: baseTicketData,
            isWinMore
          };
        }),
        catchError(() =>
          of({
            syndicateId,
            ticketData: baseTicketData,
            isWinMore
          })
        )
      );
    }

    this.addOnOfferProducsts.push(...ticketRes.product);

    return of({
      syndicateId,
      ticketData: ticketRes,
      isWinMore
    });
  }

  private fetchAddonOffersSequentially(
    userId: string,
    syndicateDetails: Data
  ): Observable<Data[]> {
    if (!this.addonOfferCpIdsArray?.length) {
      return of([]);
    }

    return new Observable<Data[]>((observer) => {
      const processNext = () => {
        if (this.currentAddonOfferIndex >= this.addonOfferIdLength) {
          observer.next(this.addOnOfferProducsts);
          observer.complete();
          return;
        }

        const cpId = this.extractAddonCpId(this.addonOfferCpIdsArray[this.currentAddonOfferIndex]);
        this.currentAddonOfferIndex++;

        if (!cpId) {
          processNext();
          return;
        }

        this.loadAddonTicketProducts(userId, cpId, syndicateDetails)
          .pipe(take(1))
          .subscribe({
            next: (products: Data[]) => {
              if (products?.length) {
                this.addOnOfferProducsts.push(...products);
              }
              processNext();
            },
            error: () => processNext(),
          });
      };

      processNext();
    });
  }

  /** Loads ticket products for one addon CP; skips jackpot-trigger purchases (no syndicate ticket). */
  private loadAddonTicketProducts(
    userId: string,
    customerPurchaseId: string,
    syndicateDetails: Data
  ): Observable<Data[]> {
    return this.fetchCustomerPurchaseSnapshot(userId, customerPurchaseId).pipe(
      take(1),
      switchMap((cpData) => {
        if (!cpData || cpData.is_jackpot_trigger_purchase === true) {
          return of([]);
        }

        const existingSyndicateId = cpData?.api_response?.response?.encrypt_syndicate_id;
        if (this.isValidEncryptSyndicateId(existingSyndicateId)) {
          return this.getTicketData(userId, existingSyndicateId, syndicateDetails).pipe(
            map((res) => res?.product ?? []),
            defaultIfEmpty([]),
            catchError(() => of([]))
          );
        }

        return interval(1000).pipe(
          switchMap(() => this.getSyndicateIdFromCustomerPurchase(userId, customerPurchaseId, false)),
          filter((syndicateId) => this.isValidEncryptSyndicateId(syndicateId)),
          take(1),
          timeout(20000),
          catchError(() => of(null)),
          switchMap((addonSyndicateId) => {
            if (!addonSyndicateId) {
              return of([]);
            }
            return this.getTicketData(userId, addonSyndicateId, syndicateDetails).pipe(
              map((res) => res?.product ?? [])
            );
          })
        );
      }),
      catchError(() => of([]))
    );
  }

  private normalizeAddonCpIds(addonEntries: AddonCustomerPurchaseEntry[]): string[] {
    if (!Array.isArray(addonEntries) || !addonEntries.length) {
      return [];
    }

    return addonEntries
      .filter((entry) => this.shouldIncludeAddonOnTicket(entry))
      .map((entry) => this.extractAddonCpId(entry))
      .filter((id): id is string => !!id);
  }

  /** Jackpot-trigger add-ons are stored on the CP but must not appear on the ticket carousel. */
  private shouldIncludeAddonOnTicket(entry: AddonCustomerPurchaseEntry): boolean {
    if (entry == null) return false;
    if (typeof entry === "string" || typeof entry === "number") {
      return true;
    }
    if (typeof entry !== "object") return false;

    return entry.is_jackpot_trigger_purchase !== true;
  }

  private extractAddonCpId(entry: AddonCustomerPurchaseEntry): string | null {
    if (!entry) return null;
    if (typeof entry === "string") return entry;
    if (typeof entry === "number") return String(entry);
    if (typeof entry !== "object") return null;

    const rawId =
      entry?.customer_purchase_id ??
      entry?.encrypted_customer_purchase_id ??
      entry?.encrypted_cp_id ??
      entry?.encrypt_id ??
      entry?.customerPurchaseId ??
      entry?.cp_id ??
      entry?.child_customer_purchase_id ??
      entry?.id ??
      null;

    return rawId != null ? String(rawId) : null;
  }

  private isValidEncryptSyndicateId(syndicateId: string | null | undefined): boolean {
    if (!syndicateId) return false;
    const normalized = String(syndicateId).trim();
    return normalized !== "" && normalized !== "0" && normalized !== "null";
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}